import React, { forwardRef } from 'react';
import { StyledButton } from './styles';
import { ProgressBar, LoadingSpinner } from './LoadingElements';
import { IconContainer } from './containers';
import Icon from '../../Icon';
import Typo from '../../Typo';
import ButtonLink from './ButtonLink';
import { Tooltip } from '@mui/material';

/**
 * SimpleButton - A clean, modular button component
 * 
 * @param {Object} props
 * @param {string} props.variant - Button style: 'primary', 'secondary', 'tertiary', 'ghost', 'themed', 'asText'
 * @param {string} props.size - Button size: 'small', 'medium', 'large'
 * @param {string} props.startIcon - Name of icon to display before text (e.g. 'Search', 'Download')
 * @param {string} props.endIcon - Name of icon to display after text (e.g. 'ArrowRight', 'Check')
 * @param {string} props.icon - Name of icon for icon-only button (e.g. 'Search', 'Add')
 * @param {string} props.themeColor - Custom theme color (for themed variant)
 * @param {string} props.bgColor - Custom bg color
 * @param {boolean} props.fullWidth - Whether button takes full width
 * @param {boolean} props.isLoading - Whether button is in loading state
 * @param {number} props.loadingProgress - Progress percentage (0-100) for the loading indicator
 * @param {boolean} props.showProgress - Whether to show progress bar (default true if loadingProgress > 0)
 * @param {string} props.loadingText - Text to show when button is loading (defaults to children)
 * @param {boolean} props.square - Whether to use square corners instead of rounded
 * @param {string} props.aria-label - Required for icon-only buttons for accessibility
 * @param {string} props.tooltipPlacement - Position of tooltip: 'top', 'bottom', 'left', 'right'
 */

// Shared utility function for icon size
const getIconSize = (size) => {
  switch(size) {
    case 'x-small': return 'x-small';
    case 'small': return 'x-small';
    case 'large': return 'small';
    default: return 'small';
  }
};

const SimpleButton = forwardRef(({
  children,
  variant='primary',
  size = 'medium',
  startIcon,
  endIcon,
  icon, // For icon-only buttons
  themeColor,
  bgColor, // Add bgColor prop
  fullWidth = false,
  justifyContent,
  isLoading = false,
  loadingProgress = 0,
  showProgress,
  loadingText,
  square = false,
  onClick,
  href,
  noTooltip = false,
  newTab,
  tooltipPlacement = 'bottom',
  ...rest
}, ref) => {
  // If href is provided and button is not disabled/loading, render a ButtonLink
  if (href && !rest.disabled && !isLoading) {
    return (
      <ButtonLink
        variant={variant}
        size={size}
        themeColor={themeColor}
        bgColor={bgColor}
        fullWidth={fullWidth}
        square={square}
        href={href}
        newTab={newTab}
        onClick={onClick}
        tooltipPlacement={tooltipPlacement}
        {...rest}
      >
        {children}
      </ButtonLink>
    );
  }
  
  // Check if this is an icon-only button
  const isIconOnly = !children && (icon || startIcon);
  const iconToUse = icon || startIcon;
  
  // Get the icon components if icon names are provided
  const IconComponent = isIconOnly && iconToUse && Icon[iconToUse];
  const StartIconComponent = !isIconOnly && startIcon && Icon[startIcon];
  const EndIconComponent = !isIconOnly && endIcon && Icon[endIcon];
  
  // Determine icon size based on button size
  const iconSize = getIconSize(size);
  
  // Determine whether to show progress bar
  const displayProgress = isLoading && 
    (showProgress || (loadingProgress > 0 && showProgress !== false));
  
  // For icon-only buttons, require an aria-label for accessibility
  if (isIconOnly && !rest['aria-label']) {
    console.warn('Icon-only buttons should have an aria-label for accessibility');
  }
  
  // Text to display during loading
  const buttonText = isLoading && loadingText ? loadingText : children;
  
  // Filter out custom props that should not be passed to DOM
  const { disabled, isLoading: _, ...filteredRest } = rest;
  
  // Create the button element
  const buttonElement = (
    <StyledButton
      ref={ref}
      type='button'
      onClick={onClick}
      aria-busy={isLoading}
      disabled={disabled || isLoading}
      $variant={variant}
      $size={size}
      $themeColor={themeColor}
      $bgColor={bgColor}
      $fullWidth={!isIconOnly && fullWidth}
      $justifyContent={justifyContent}
      $iconOnly={isIconOnly}
      $square={square}
      $isLoading={isLoading}
      {...filteredRest}
    >
      {displayProgress && (
        <ProgressBar value={loadingProgress} />
      )}
      
      {isIconOnly ? 
        <IconContainer $isLoading={isLoading}>
          {isLoading ? (
            <LoadingSpinner size={size} />
          ) : (
            IconComponent && <IconComponent size={size} />
          )}
        </IconContainer>
        : 
        <>
          {isLoading ? (
            <LoadingSpinner size={size} />
          ) : (
            StartIconComponent && (
              <IconContainer>
                <StartIconComponent size={iconSize} />
              </IconContainer>
            )
          )}
          <Typo.MediumText fontWeight='500' $textAlign='center'>
            {buttonText}
          </Typo.MediumText>
          {!isLoading && EndIconComponent && (
            <IconContainer>
              <EndIconComponent size={iconSize} />
            </IconContainer>
          )}
        </>
      }
    </StyledButton>
  );

  // For icon-only buttons, wrap in a tooltip if there's an aria-label
  if (isIconOnly && !noTooltip) {
    return (
      <Tooltip title={rest['aria-label'] || icon} placement={tooltipPlacement}>
        {buttonElement}
      </Tooltip>
    );
  }

  // Return regular button for non-icon buttons
  return buttonElement;
});

export default SimpleButton;